import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';
import { useFacilityStore } from '../../../app/z-store/store';

const STATUS_CLASS = {
  Generated: 'bg-success',
  Missed: 'bg-danger',
  'Due This Month': 'bg-warning',
  Upcoming: 'bg-secondary',
};

const MONTH_LABELS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-');
const fmtMoney = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const StatusBadge = ({ status }) => (
  <span className={`badge ${STATUS_CLASS[status] || 'bg-secondary'}`}>{status}</span>
);

const MonthGrid = ({ grid }) => (
  <div className="d-flex gap-1">
    {grid.map((m, i) => {
      const cls = m.invoiced ? 'bg-success text-white' : m.expected ? 'border border-warning text-warning' : 'bg-light text-muted';
      const title = m.invoiced ? `${m.yearMonth}: ${m.invoiceNumber}` : m.expected ? `${m.yearMonth}: invoice expected` : m.yearMonth;
      return (
        <span key={m.yearMonth} title={title} className={`d-inline-block text-center rounded ${cls}`} style={{ width: 22, fontSize: 11, lineHeight: '22px' }}>
          {MONTH_LABELS[i]}
        </span>
      );
    })}
  </div>
);

const LevyForecastTab = () => {
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(currentYear);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState(null);
  const [expandedRows, setExpandedRows] = useState(null);

  const yearOptions = [-1, 0, 1, 2].map((o) => ({ label: String(currentYear + o), value: currentYear + o }));
  const statusOptions = [
    { label: 'All', value: 'all' },
    { label: 'Has missed invoices', value: 'missed' },
    { label: 'Due this month', value: 'due' },
    { label: 'Fully invoiced', value: 'complete' },
  ];

  useEffect(() => {
    if (!selectedFacilityId) return;
    (async () => {
      try {
        setLoading(true);
        const response = await makeRequest2(
          `/api/app/levy_management/${selectedFacilityId}/levy-contracts/forecast?year=${year}`,
          'GET'
        );
        if (response.success) {
          setContracts(response.data.data?.contracts || []);
          setStats(response.data.data?.statistics || null);
        } else {
          toastify('Failed to fetch levy forecast', 'error');
        }
      } catch (error) {
        toastify('Error fetching levy forecast', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedFacilityId, year]);

  const visible = contracts.filter((c) => {
    const term = searchTerm.toLowerCase();
    if (term && ![c.customer?.fullName, c.unitName, c.levyName, c.contractName].some((v) => v?.toLowerCase().includes(term))) return false;
    if (statusFilter === 'missed') return c.summary?.missed > 0;
    if (statusFilter === 'due') return c.summary?.dueThisMonth > 0;
    if (statusFilter === 'complete') return c.summary && c.summary.remaining === 0;
    return true;
  });

  const contractRow = (c) => [
    c.customer?.fullName || '',
    c.unitName || '',
    c.levyName || '',
    `${c.frequency} / ${c.billingType}`,
    fmtDate(c.startDate),
    c.openEnded ? 'Open-ended' : fmtDate(c.endDate),
    c.summary ? c.summary.generated : '',
    c.summary ? c.summary.totalInvoices : '',
    c.summary ? c.summary.remaining : 'No start date',
    c.summary ? c.summary.missed : '',
    c.summary?.nextInvoice ? `${c.summary.nextInvoice.label} (${fmtDate(c.summary.nextInvoice.expectedIssueDate)})` : '',
    c.summary ? c.summary.remainingValue : '',
  ];
  const contractHead = ['Customer', 'Unit', 'Levy', 'Agreement', 'Start date', 'End date', 'Generated', 'Invoices in term', 'Remaining', 'Missed', 'Next invoice', 'Remaining value'];
  const scheduleHead = ['Customer', 'Unit', 'Levy', 'Period', 'Covers', 'Expected invoice date', 'Amount', 'Invoice no.', 'Invoice status', 'Forecast'];
  const scheduleRows = visible.flatMap((c) => (c.periods || []).map((p) => [
    c.customer?.fullName || '', c.unitName || '', c.levyName || '', p.label,
    `${fmtDate(p.periodStart)} - ${fmtDate(p.periodEnd)}`, fmtDate(p.expectedIssueDate),
    p.invoice?.totalAmount ?? p.expectedAmount, p.invoice?.invoiceNumber || '', p.invoice?.status || '', p.status,
  ]));

  const exportExcel = () => {
    if (!visible.length) return toastify('Nothing to export', 'warning');
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([contractHead, ...visible.map(contractRow)]), 'Forecast Summary');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([scheduleHead, ...scheduleRows]), 'Invoice Schedule');
    XLSX.writeFile(wb, `levy-forecast-${year}.xlsx`);
  };

  const exportPdf = () => {
    if (!visible.length) return toastify('Nothing to export', 'warning');
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(14);
    doc.text(`Levy Forecast ${year}`, 14, 14);
    doc.setFontSize(9);
    doc.text(`Generated ${new Date().toLocaleString('en-GB')}`, 14, 20);
    autoTable(doc, {
      startY: 25,
      head: [contractHead],
      body: visible.map((c) => contractRow(c).map((v, i) => (i === 11 && v !== '' ? fmtMoney(v) : v))),
      styles: { fontSize: 7 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
    });
    doc.addPage();
    doc.setFontSize(12);
    doc.text('Invoice Schedule', 14, 14);
    autoTable(doc, {
      startY: 20,
      head: [scheduleHead],
      body: scheduleRows.map((r) => r.map((v, i) => (i === 6 ? fmtMoney(v) : v))),
      styles: { fontSize: 7 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
    });
    doc.save(`levy-forecast-${year}.pdf`);
  };

  const rowExpansionTemplate = (c) => {
    if (!c.summary) return <div className="p-3 text-muted">This contract has no start date, so no schedule can be forecast.</div>;
    return (
      <div className="p-3">
        <div className="mb-3">
          <strong>{year} invoiced months</strong> <small className="text-muted">(green = invoiced, outlined = expected)</small>
          <div className="mt-1"><MonthGrid grid={c.monthGrid} /></div>
        </div>
        {c.openEnded && (
          <p className="text-muted mb-2"><small>No end date: schedule is projected to 31 Dec {year}.</small></p>
        )}
        {c.unassignedInvoices > 0 && (
          <p className="text-warning mb-2"><small>{c.unassignedInvoices} invoice(s) could not be matched to a billing period.</small></p>
        )}
        <DataTable value={c.periods} size="small" stripedRows>
          <Column field="label" header="Period" />
          <Column header="Covers" body={(p) => `${fmtDate(p.periodStart)} - ${fmtDate(p.periodEnd)}`} />
          <Column header="Expected invoice date" body={(p) => fmtDate(p.expectedIssueDate)} />
          <Column header="Amount" body={(p) => fmtMoney(p.invoice?.totalAmount ?? p.expectedAmount)} />
          <Column header="Invoice" body={(p) => p.invoice?.invoiceNumber || '-'} />
          <Column header="Invoice status" body={(p) => p.invoice?.status || '-'} />
          <Column header="Forecast" body={(p) => <StatusBadge status={p.status} />} />
        </DataTable>
      </div>
    );
  };

  const statTile = (label, value) => (
    <div className="col-6 col-md-2" key={label}>
      <div className="card mb-2"><div className="card-body py-2">
        <small className="text-muted">{label}</small>
        <h5 className="mb-0">{value}</h5>
      </div></div>
    </div>
  );

  return (
    <div>
      {stats && (
        <div className="row">
          {statTile('Contracts', stats.totalContracts)}
          {statTile('Invoices in term', stats.totalInvoices)}
          {statTile('Generated', stats.generated)}
          {statTile('Remaining', stats.remaining)}
          {statTile('Missed', stats.missed)}
          {statTile('Remaining value', fmtMoney(stats.remainingValue))}
        </div>
      )}

      <div className="row mb-3">
        <div className="col-md-4">
          <input className="form-control" placeholder="Search customer, unit, levy or contract" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="col-md-2"><Dropdown className="w-100" value={year} options={yearOptions} onChange={(e) => setYear(e.value)} /></div>
        <div className="col-md-3"><Dropdown className="w-100" value={statusFilter} options={statusOptions} onChange={(e) => setStatusFilter(e.value)} /></div>
        <div className="col-md-3 d-flex gap-2 justify-content-end">
          <button type="button" className="btn btn-success" onClick={exportExcel} disabled={loading || !visible.length}>
            <i className="ti ti-file-spreadsheet me-1"></i>Excel
          </button>
          <button type="button" className="btn btn-danger" onClick={exportPdf} disabled={loading || !visible.length}>
            <i className="ti ti-file-type-pdf me-1"></i>PDF
          </button>
        </div>
      </div>

      <DataTable
        value={visible}
        loading={loading}
        dataKey="contractId"
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        paginator
        rows={10}
        stripedRows
        emptyMessage="No levy contracts found."
      >
        <Column expander style={{ width: '3rem' }} />
        <Column header="Customer" body={(c) => c.customer?.fullName} />
        <Column field="unitName" header="Unit" />
        <Column field="levyName" header="Levy" />
        <Column header="Agreement" body={(c) => `${c.frequency} / ${c.billingType}`} />
        <Column header="Start date" body={(c) => fmtDate(c.startDate)} />
        <Column header="End date" body={(c) => (c.openEnded ? 'Open-ended' : fmtDate(c.endDate))} />
        <Column header="Generated" body={(c) => c.summary ? `${c.summary.generated} / ${c.summary.totalInvoices}` : '-'} />
        <Column header="Remaining" body={(c) => c.summary ? <strong>{c.summary.remaining}</strong> : 'No start date'} />
        <Column header="Next invoice" body={(c) => c.summary?.nextInvoice ? `${c.summary.nextInvoice.label} (${fmtDate(c.summary.nextInvoice.expectedIssueDate)})` : '-'} />
        <Column header="Missed" body={(c) => c.summary?.missed ? <span className="badge bg-danger">{c.summary.missed}</span> : '-'} />
      </DataTable>
    </div>
  );
};

export default LevyForecastTab;
